import { useEffect, useMemo, useState } from 'react';
import { Sparkles, MessageSquare, FileText, Copy, Check, Wand2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { usePersonasStore } from '@/stores/personasStore';
import api from '@/lib/api';

type ContentType = 'post' | 'comment' | 'reply';
type OutputLength = 'short' | 'medium' | 'long';

interface GenerateResponse {
  id: string;
  content: string;
  variations: string[];
  credits_used: number;
  tokens?: {
    input_tokens: number;
    output_tokens: number;
    total_tokens: number;
  };
  model?: string;
}

interface AnalyzeStyleResponse {
  data: {
    writing_style: string;
    vocabulary_level: string;
    style_score: number;
  };
}

interface ContentHistoryItem {
  id: string;
  type: string;
  content_type: string | null;
  original_input: string | null;
  content: string;
  model_used: string | null;
  tokens_input: number;
  tokens_output: number;
  credits_spent: number;
  status: string;
  created_at: string;
}

export function StudioPage() {
  const { personas, selectedPersona, setSelectedPersona, fetchPersonas } = usePersonasStore();
  const [contentType, setContentType] = useState<ContentType>('post');
  const [prompt, setPrompt] = useState('');
  const [generatedContent, setGeneratedContent] = useState('');
  const [variations, setVariations] = useState<string[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUsage, setLastUsage] = useState<GenerateResponse['tokens'] | null>(null);
  const [lastModel, setLastModel] = useState<string | null>(null);
  const [lastCreditsUsed, setLastCreditsUsed] = useState<number | null>(null);
  const [styleAnalysisTargetUrl, setStyleAnalysisTargetUrl] = useState('');
  const [styleAnalysisSummary, setStyleAnalysisSummary] = useState<string | null>(null);
  const [framework, setFramework] = useState('');
  const [archetype, setArchetype] = useState('');
  const [length, setLength] = useState<OutputLength>('medium');
  const [history, setHistory] = useState<ContentHistoryItem[]>([]);

  useEffect(() => {
    void fetchPersonas();
    void fetchHistory();
  }, [fetchPersonas]);

  const fetchHistory = async () => {
    try {
      const response = await api.get<{ data: ContentHistoryItem[] }>('/content/history');
      setHistory(response.data.data ?? []);
    } catch (_error) {
      // Keep history optional in UI.
    }
  };

  const endpoint = useMemo(() => {
    if (contentType === 'comment') {
      return '/generate/comment';
    }
    return '/generate/post';
  }, [contentType]);

  const handleGenerate = async () => {
    if (!prompt.trim() || !selectedPersona) {
      return;
    }

    setError(null);
    setIsGenerating(true);

    try {
      const response = await api.post<{ data: GenerateResponse }>(endpoint, {
        content_type: contentType === 'reply' ? 'comment' : contentType,
        prompt: prompt.trim(),
        topic: prompt.trim(),
        persona_id: selectedPersona.id,
        tone: selectedPersona.tone,
        language: selectedPersona.language,
        framework: framework.trim() || undefined,
        archetype: archetype.trim() || undefined,
        length,
        include_hashtags: contentType !== 'comment',
        include_emoji: true,
        variations: 2,
      });

      const payload = response.data.data;
      setGeneratedContent(payload.content || '');
      setVariations(payload.variations || []);
      setLastUsage(payload.tokens ?? null);
      setLastModel(payload.model ?? null);
      setLastCreditsUsed(payload.credits_used ?? null);
      void fetchHistory();
    } catch (requestError: unknown) {
      if (
        typeof requestError === 'object' &&
        requestError !== null &&
        'response' in requestError &&
        typeof (requestError as { response?: { data?: { error?: string } } }).response === 'object'
      ) {
        const message = (requestError as { response?: { data?: { error?: string } } }).response?.data?.error;
        setError(message ?? 'Generazione fallita');
      } else {
        setError('Generazione fallita');
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCopy = () => {
    if (!generatedContent) {
      return;
    }
    void navigator.clipboard.writeText(generatedContent);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleAnalyzeStyle = async () => {
    if (!styleAnalysisTargetUrl.trim()) {
      setError('Enter a LinkedIn profile URL to analyze style.');
      return;
    }

    setError(null);
    setIsGenerating(true);
    try {
      const scrapeResp = await api.post<{ data: { profile: { id: string } } }>('/linkedin/scrape', {
        linkedin_url: styleAnalysisTargetUrl.trim(),
        force_refresh: false,
      });

      const linkedinProfileId = scrapeResp.data.data.profile.id;
      const analysisResp = await api.post<AnalyzeStyleResponse>('/analyze/style', {
        linkedin_profile_id: linkedinProfileId,
      });

      const analysis = analysisResp.data.data;
      setStyleAnalysisSummary(
        `Style: ${analysis.writing_style} | Vocabulary: ${analysis.vocabulary_level} | Score: ${analysis.style_score}`
      );
    } catch (_requestError) {
      setError('Style analysis failed');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">AI Studio</h1>
        <p className="text-muted-foreground">Generate LinkedIn content with AI</p>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Left panel - Controls */}
        <div className="lg:col-span-2 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Content Generator</CardTitle>
              <CardDescription>What would you like to create?</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Content Type Selector */}
              <div className="flex gap-2">
                {(['post', 'comment', 'reply'] as ContentType[]).map((type) => (
                  <Button
                    key={type}
                    variant={contentType === type ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setContentType(type)}
                  >
                    {type === 'post' && <FileText className="mr-2 h-4 w-4" />}
                    {type === 'comment' && <MessageSquare className="mr-2 h-4 w-4" />}
                    {type === 'reply' && <Sparkles className="mr-2 h-4 w-4" />}
                    {type.charAt(0).toUpperCase() + type.slice(1)}
                  </Button>
                ))}
              </div>

              {/* Persona Selector */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Persona</label>
                <select
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
                  value={selectedPersona?.id || ''}
                  onChange={(e) => {
                    const persona = personas.find(p => p.id === e.target.value);
                    setSelectedPersona(persona || null);
                  }}
                >
                  {personas.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} ({p.tone})
                    </option>
                  ))}
                </select>
              </div>

              {/* Prompt Input */}
              <div className="space-y-2">
                <label className="text-sm font-medium">What do you want to write about?</label>
                <textarea
                  className="flex min-h-[120px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  placeholder={`Describe your ${contentType} idea...`}
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                />
              </div>

              <div className="grid gap-3 md:grid-cols-3">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Framework</label>
                  <input
                    className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
                    value={framework}
                    onChange={(event) => setFramework(event.target.value)}
                    placeholder="AIDA, PAS..."
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Archetype</label>
                  <input
                    className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
                    value={archetype}
                    onChange={(event) => setArchetype(event.target.value)}
                    placeholder="Mentor, Builder..."
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Length</label>
                  <select
                    className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
                    value={length}
                    onChange={(event) => setLength(event.target.value as OutputLength)}
                  >
                    <option value="short">Short</option>
                    <option value="medium">Medium</option>
                    <option value="long">Long</option>
                  </select>
                </div>
              </div>

              {error ? <p className="text-sm text-destructive">{error}</p> : null}

              <div className="rounded-md border p-3 space-y-2">
                <label className="text-sm font-medium">Analyze LinkedIn style (optional)</label>
                <div className="flex flex-col sm:flex-row gap-2">
                  <input
                    className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
                    placeholder="https://www.linkedin.com/in/username"
                    value={styleAnalysisTargetUrl}
                    onChange={(event) => setStyleAnalysisTargetUrl(event.target.value)}
                  />
                  <Button variant="outline" onClick={() => void handleAnalyzeStyle()} disabled={isGenerating}>
                    Analyze style
                  </Button>
                </div>
                {styleAnalysisSummary ? (
                  <p className="text-xs text-muted-foreground">{styleAnalysisSummary}</p>
                ) : null}
              </div>

              {/* Generate Button */}
              <div className="flex items-center justify-between">
                <div className="text-sm text-muted-foreground">
                  {lastModel ? `Model: ${lastModel}` : 'Generation via Gemini 2.5 Flash'}
                </div>
                <Button
                  onClick={handleGenerate}
                  disabled={isGenerating || !prompt.trim() || !selectedPersona}
                >
                  {isGenerating ? (
                    <>
                      <Wand2 className="mr-2 h-4 w-4 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="mr-2 h-4 w-4" />
                      Generate
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Generated Content */}
          {generatedContent && (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Generated Content</CardTitle>
                <Button variant="ghost" size="sm" onClick={handleCopy}>
                  {copied ? (
                    <>
                      <Check className="mr-2 h-4 w-4" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="mr-2 h-4 w-4" />
                      Copy
                    </>
                  )}
                </Button>
              </CardHeader>
              <CardContent>
                <div className="rounded-md bg-muted p-4">
                  <pre className="whitespace-pre-wrap text-sm font-mono">{generatedContent}</pre>
                </div>
                {variations.length > 0 ? (
                  <div className="mt-4 space-y-2">
                    <p className="text-sm font-medium">Variations</p>
                    {variations.map((item, index) => (
                      <div key={`variation-${index}`} className="rounded-md border p-3 text-sm">
                        {item}
                      </div>
                    ))}
                  </div>
                ) : null}

                {lastUsage ? (
                  <div className="mt-4 rounded-md border p-3 text-xs text-muted-foreground">
                    <p>Token usage: {lastUsage.input_tokens} in / {lastUsage.output_tokens} out / {lastUsage.total_tokens} total</p>
                    <p>Credits used: {lastCreditsUsed ?? '-'}</p>
                  </div>
                ) : null}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right panel - Tips & History */}
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Tips</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <p className="text-muted-foreground">
                • Be specific about what you want to communicate
              </p>
              <p className="text-muted-foreground">
                • Use framework + archetype for sharper output
              </p>
              <p className="text-muted-foreground">
                • Switch personas to match different contexts
              </p>
              <p className="text-muted-foreground">
                • Always review before posting
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Model</CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground">gemini-2.5-flash</CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Recent Output</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {history.length === 0 ? (
                <p className="text-sm text-muted-foreground">No generated content yet.</p>
              ) : (
                history.slice(0, 5).map((item) => (
                  <div key={item.id} className="rounded-md border p-2">
                    <p className="text-xs text-muted-foreground">
                      {item.type} | {item.model_used ?? 'n/a'} | {item.credits_spent} credits
                    </p>
                    <p className="mt-1 text-sm line-clamp-3">{item.content}</p>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
